import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, CalendarPlus, Check, Send, LogIn, ImagePlus, X, Loader2, Play } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES } from "@/lib/constants";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const emptyPlace = { name: "", category: "Food & Drink", city: "Aiken", address: "", short_tagline: "", description: "", website: "", phone: "", cover_image: "", photos: [] };
const emptyEvent = { title: "", start_datetime: "", venue_name: "", address: "", category: "Entertainment", description: "", ticket_url: "", cover_image: "" };
const emptyVideo = { title: "", platform: "youtube", url: "", thumbnail: "", submitter_note: "", feature: false };

function PhotoGallery({ photos, uploading, onAdd, onRemove, max = 6 }) {
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-muted-foreground">Photos <span className="font-normal">(optional, up to {max} — first one is the cover)</span></span>
      <div className="flex flex-wrap gap-2">
        {photos.map((ph, i) => (
          <div key={ph} className="relative h-16 w-16">
            <img src={ph} alt="" className="h-16 w-16 rounded-lg object-cover" />
            {i === 0 && <span className="absolute left-0 top-0 rounded-br rounded-tl-lg bg-primary px-1 text-[9px] font-semibold text-primary-foreground">Cover</span>}
            <button type="button" onClick={() => onRemove(ph)} data-testid={`submit-photo-remove-${i}`}
              className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow">
              <X size={11} />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground hover:border-primary">
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <ImagePlus size={18} />}
            <input type="file" accept="image/*" className="hidden" onChange={onAdd} data-testid="submit-place-photos" />
          </label>
        )}
      </div>
    </div>
  );
}

function CoverUpload({ image, uploading, onPick, testid }) {
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-muted-foreground">Cover photo <span className="font-normal">(optional)</span></span>
      <div className="flex items-center gap-3">
        {image
          ? <img src={image} alt="" className="h-16 w-16 rounded-lg object-cover" />
          : <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground"><ImagePlus size={20} /></div>}
        <label className="cursor-pointer rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-primary">
          {uploading ? "Uploading…" : image ? "Change photo" : "Upload photo"}
          <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={testid} />
        </label>
      </div>
    </div>
  );
}

function TextField({ label, required, ...props }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-muted-foreground">{label}{required && <span className="text-primary"> *</span>}</span>
      <input {...props} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
    </label>
  );
}

export default function Submit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("place");
  const [place, setPlace] = useState(emptyPlace);
  const [event, setEvent] = useState(emptyEvent);
  const [video, setVideo] = useState(emptyVideo);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadCover = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      const url = `${BACKEND}${data.url}`;
      if (kind === "place") setPlace((p) => { const photos = [...(p.photos || []), url]; return { ...p, photos, cover_image: p.cover_image || photos[0] }; });
      else setEvent((ev) => ({ ...ev, cover_image: url }));
      toast.success("Photo added");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removePlacePhoto = (url) => setPlace((p) => { const photos = (p.photos || []).filter((x) => x !== url); return { ...p, photos, cover_image: photos[0] || "" }; });
  const uploadPoster = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setVideo((v) => ({ ...v, thumbnail: `${BACKEND}${data.url}` }));
      toast.success("Poster added");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (user === null) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  if (user === false) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="submit-login-gate">
        <Store className="mx-auto mb-4 text-primary" size={40} />
        <h1 className="font-serif text-3xl font-bold">Add your business or event</h1>
        <p className="mt-2 text-muted-foreground">Sign in to submit a listing. It's free — our team reviews every submission before it goes live.</p>
        <button onClick={() => navigate("/auth")} data-testid="submit-signin-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
          <LogIn size={16} /> Sign in to continue
        </button>
      </div>
    );
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      if (mode === "place") {
        if (!place.name) { setSubmitting(false); return toast.error("Business name is required"); }
        await api.post("/submissions/place", place);
      } else if (mode === "event") {
        if (!event.title || !event.start_datetime) { setSubmitting(false); return toast.error("Event title and date are required"); }
        await api.post("/submissions/event", event);
      } else {
        if (!video.title || !video.url) { setSubmitting(false); return toast.error("Add a title and a video link"); }
        if ((video.platform === "tiktok" || video.platform === "instagram") && !video.thumbnail) { setSubmitting(false); return toast.error("Add a poster image for TikTok/Instagram videos"); }
        if (video.feature) {
          await api.post("/videos/feature-request", { title: video.title, platform: video.platform, url: video.url, thumbnail: video.thumbnail });
        } else {
          await api.post("/videos/submit", { title: video.title, platform: video.platform, url: video.url, thumbnail: video.thumbnail, submitter_note: video.submitter_note });
        }
      }
      setDone(true);
      toast.success(mode === "video" && video.feature ? "Your video is now featured on the home page for 7 days!" : "Submitted! We'll review it shortly.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong — please try again");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center" data-testid="submit-success">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10"><Check className="text-primary" size={32} /></div>
        <h1 className="font-serif text-3xl font-bold">Thanks, {user.name?.split(" ")[0] || "friend"}!</h1>
        <p className="mt-2 text-muted-foreground">Your submission is in our review queue. You can track its status under <b>Profile → My Submissions</b>.</p>
        <div className="mt-6 flex justify-center gap-3">
          <button onClick={() => { setDone(false); setPlace(emptyPlace); setEvent(emptyEvent); setVideo(emptyVideo); }} data-testid="submit-another-btn"
            className="rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold hover:border-primary">Submit another</button>
          <button onClick={() => navigate("/profile")} className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">View my submissions</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 md:px-8" data-testid="submit-page">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Community portal</span>
      <h1 className="font-serif text-4xl font-bold tracking-tight">Add a business or event</h1>
      <p className="mt-2 text-muted-foreground">Know a great local spot, sports facility, or upcoming event we're missing? Send it our way — it's free and we review everything before it publishes.</p>

      <div className="mt-6 flex gap-2 rounded-full border border-border bg-card p-1">
        <button onClick={() => setMode("place")} data-testid="submit-mode-place"
          className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${mode === "place" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          <Store size={16} /> Business / Place
        </button>
        <button onClick={() => setMode("event")} data-testid="submit-mode-event"
          className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${mode === "event" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          <CalendarPlus size={16} /> Event
        </button>
        <button onClick={() => setMode("video")} data-testid="submit-mode-video"
          className={`flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors ${mode === "video" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
          <Play size={16} /> Video
        </button>
      </div>

      <div className="mt-6 space-y-3 rounded-2xl border border-border bg-card p-5">
        {mode === "place" ? (
          <>
            <TextField label="Business / place name" required data-testid="submit-place-name" value={place.name}
              onChange={(e) => setPlace({ ...place, name: e.target.value })} placeholder="e.g. Aiken Coffee Roasters" />
            <PhotoGallery photos={place.photos || []} uploading={uploading} onAdd={(e) => uploadCover(e, "place")} onRemove={removePlacePhoto} />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Category</span>
              <select value={place.category} data-testid="submit-place-category" onChange={(e) => setPlace({ ...place, category: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField label="City" data-testid="submit-place-city" value={place.city} onChange={(e) => setPlace({ ...place, city: e.target.value })} />
              <TextField label="Address" value={place.address} onChange={(e) => setPlace({ ...place, address: e.target.value })} />
            </div>
            <TextField label="Short tagline" value={place.short_tagline} onChange={(e) => setPlace({ ...place, short_tagline: e.target.value })} placeholder="One line that sums it up" />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Description</span>
              <textarea rows={3} value={place.description} data-testid="submit-place-description" onChange={(e) => setPlace({ ...place, description: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField label="Website" value={place.website} onChange={(e) => setPlace({ ...place, website: e.target.value })} placeholder="https://" />
              <TextField label="Phone" value={place.phone} onChange={(e) => setPlace({ ...place, phone: e.target.value })} />
            </div>
          </>
        ) : mode === "event" ? (
          <>
            <TextField label="Event title" required data-testid="submit-event-title" value={event.title} onChange={(e) => setEvent({ ...event, title: e.target.value })} />
            <CoverUpload image={event.cover_image} uploading={uploading} onPick={(e) => uploadCover(e, "event")} testid="submit-event-cover" />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Date & time<span className="text-primary"> *</span></span>
              <input type="datetime-local" value={event.start_datetime} data-testid="submit-event-datetime" onChange={(e) => setEvent({ ...event, start_datetime: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField label="Venue name" value={event.venue_name} onChange={(e) => setEvent({ ...event, venue_name: e.target.value })} />
              <TextField label="Address" value={event.address} onChange={(e) => setEvent({ ...event, address: e.target.value })} />
            </div>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Category</span>
              <select value={event.category} data-testid="submit-event-category" onChange={(e) => setEvent({ ...event, category: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Description</span>
              <textarea rows={3} value={event.description} data-testid="submit-event-description" onChange={(e) => setEvent({ ...event, description: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <TextField label="Ticket / info link" value={event.ticket_url} onChange={(e) => setEvent({ ...event, ticket_url: e.target.value })} placeholder="https://" />
          </>
        ) : (
          <>
            <TextField label="Video title" required data-testid="submit-video-title" value={video.title}
              onChange={(e) => setVideo({ ...video, title: e.target.value })} placeholder="e.g. Sunset at Hitchcock Woods" />
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">Platform</span>
              <select value={video.platform} data-testid="submit-video-platform" onChange={(e) => setVideo({ ...video, platform: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                <option value="youtube">YouTube / Shorts</option>
                <option value="tiktok">TikTok</option>
                <option value="instagram">Instagram Reel</option>
                <option value="facebook">Facebook (post or video)</option>
              </select>
            </label>
            <TextField label="Video link" required data-testid="submit-video-url" value={video.url}
              onChange={(e) => setVideo({ ...video, url: e.target.value })} placeholder="Paste the share link to your clip" />
            <div>
              <span className="mb-1 block text-sm font-medium text-muted-foreground">Poster image {(video.platform === "tiktok" || video.platform === "instagram") ? <span className="text-primary">*</span> : <span className="font-normal">(optional — YouTube auto-fills)</span>}</span>
              <div className="flex items-center gap-3">
                {video.thumbnail
                  ? <img src={video.thumbnail} alt="" className="h-16 w-16 rounded-lg object-cover" />
                  : <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground"><ImagePlus size={20} /></div>}
                <label className="cursor-pointer rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:border-primary">
                  {uploading ? "Uploading…" : video.thumbnail ? "Change poster" : "Upload poster"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPoster} data-testid="submit-video-poster" />
                </label>
              </div>
              {(video.platform === "tiktok" || video.platform === "instagram") && <p className="mt-1 text-xs text-muted-foreground">TikTok & Instagram can't auto-generate a preview — please add a screenshot frame.</p>}
            </div>
            <label className="flex items-start gap-2 rounded-xl border border-border bg-card p-3 text-sm">
              <input type="checkbox" checked={!!video.feature} data-testid="submit-video-feature" onChange={(e) => setVideo({ ...video, feature: e.target.checked })} className="mt-0.5" />
              <span><b className="flex items-center gap-1"><Play size={14} className="fill-current text-primary" /> Feature on the CSRAGo home page for 7 days</b>
              <span className="block text-xs text-muted-foreground">Free for now — your clip is pinned as the big Featured Video at the top of Watch. Publishes immediately (no review).</span></span>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-muted-foreground">A note for our team <span className="font-normal">(optional)</span></span>
              <textarea rows={2} value={video.submitter_note} data-testid="submit-video-note" onChange={(e) => setVideo({ ...video, submitter_note: e.target.value })}
                placeholder="Where was it filmed? Anything we should know?"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </label>
            <p className="text-xs text-muted-foreground">Share a public link from TikTok, Instagram, YouTube or Facebook. We'll review it before it appears in the Watch section.</p>
          </>
        )}
      </div>

      <button onClick={submit} disabled={submitting} data-testid="submit-send-btn"
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">
        <Send size={16} /> {submitting ? "Submitting…" : mode === "video" && video.feature ? "Feature my video" : "Submit for review"}
      </button>
      <p className="mt-2 text-center text-xs text-muted-foreground">Free to submit. Our team reviews every listing before it appears on CSRAGo.</p>
    </div>
  );
}
